import { useContext, useEffect, useState } from "react";
import { AlternativeWithoutExisting } from "src/helpers/types";
import { LanguageContext } from "./languageContext";
import { TypographyP } from "./Typography";
type AlternativeCombination =
  | AlternativeWithoutExisting
  | "NB_Only_SB_Only_both"
  | "Converging_Diverging_both";

const resultTypes = [
  "travelTimeIncrease",
  "sideStreetVolumes",
  "volumeToCapacityRatios",
  "transitDelay",
];
type ResultTypes = (typeof resultTypes)[number];

const matrix: Record<
  ResultTypes,
  Partial<Record<AlternativeCombination, string[]>>
> = {
  travelTimeIncrease: {
    NB_Only: ["results-travelTime-NB_Only-0", "results-travelTime-NB_Only-1"],
    SB_Only: [
      "results-travelTime-SB_Only-0",
      "results-travelTime-SB_Only-1",
      "results-travelTime-SB_Only-2",
      "results-travelTime-SB_Only-3",
    ],
    Converging_Diverging: [
      "results-travelTime-Converging_Diverging-0",
      "results-travelTime-Converging_Diverging-1",
      "results-travelTime-Converging_Diverging-2",
    ],
    Converging_Diverging_Short: [
      "results-travelTime-Converging_Diverging_Short-0",
      "results-travelTime-Converging_Diverging_Short-1",
    ],
  },
  sideStreetVolumes: {
    NB_Only_SB_Only_both: ["results-sideStreets-NB_Only_SB_Only_both-0"],
    Converging_Diverging_both: [
      "results-sideStreets-Converging_Diverging_both-0",
    ],
  },
  volumeToCapacityRatios: {
    NB_Only: [
      "results-vCRatios-NB_Only-0",
      "results-vCRatios-NB_Only-1",
      "results-vCRatios-NB_Only-2",
      "results-vCRatios-NB_Only-3",
    ],
    SB_Only: [
      "results-vCRatios-SB_Only-0",
      "results-vCRatios-SB_Only-1",
      "results-vCRatios-SB_Only-2",
      "results-vCRatios-SB_Only-3",
    ],
    Converging_Diverging: [
      "results-vCRatios-Converging_Diverging-0",
      "results-vCRatios-Converging_Diverging-1",
      "results-vCRatios-Converging_Diverging-2",
      "results-vCRatios-Converging_Diverging-3",
    ],
    Converging_Diverging_Short: [
      "results-vCRatios-Converging_Diverging_Short-0",
      "results-vCRatios-Converging_Diverging_Short-1",
      "results-vCRatios-Converging_Diverging_Short-2",
      "results-vCRatios-Converging_Diverging_Short-3",
    ],
  },
  transitDelay: {
    NB_Only: ["results-transitDelay-NB_Only-0"],
    SB_Only: ["results-transitDelay-SB_Only-0"],
    Converging_Diverging_both: [
      "results-transitDelay-Converging_Diverging_both-0",
    ],
  },
};

const getMatrixTranslations = (
  resultsType: ResultTypes,
  AlternativeCombinaiton: AlternativeCombination
): string[] => {
  return matrix[resultsType]?.[AlternativeCombinaiton] ?? [];
};

const getValidAlts = (resultsType: ResultTypes): AlternativeCombination[] => {
  return Object.keys(matrix[resultsType] ?? {}) as AlternativeCombination[];
};

const altCombinationToTranslationKey: Record<AlternativeCombination, string> = {
  NB_Only: "altName-NB_Only",
  SB_Only: "altName-SB_Only",
  Converging_Diverging: "altName-Converging_Diverging",
  Converging_Diverging_Short: "altName-Converging_Diverging_Short",
  NB_Only_SB_Only_both: "altName-NB_Only_SB_Only_both",
  Converging_Diverging_both: "altName-Converging_Diverging_both",
};

export default function DecisionMatrix() {
  const { translate } = useContext(LanguageContext);
  const [selectedResultsType, setSelectedResultsType] =
    useState<ResultTypes>("travelTimeIncrease");
  const [selectedAlternative, setSelectedAlternative] =
    useState<AlternativeCombination>("NB_Only");
  const validAlts = getValidAlts(selectedResultsType);

  useEffect(() => {
    if (!validAlts.includes(selectedAlternative)) {
      setSelectedAlternative(validAlts[0]);
    }
  }, [selectedAlternative, setSelectedAlternative, validAlts]);

  if (!validAlts.length) {
    return <div>...</div>;
  }

  return (
    <div className="flex flex-col gap-4">
      <TypographyP textId="studyResultsDescription2" />
      <div className="flex flex-col md:flex-row gap-4">
        <label>
          <div className="text-sm font-bold">
            {translate("transportationMetric")}
          </div>
          <select
            className="p-3 border border-gray-300 rounded-md"
            value={selectedResultsType}
            onChange={(e) => setSelectedResultsType(e.target.value)}
          >
            {resultTypes.map((key) => (
              <option key={key} value={key}>
                {translate(key as keyof typeof translate)}
              </option>
            ))}
          </select>
        </label>
        <label>
          <div className="text-sm font-bold">
            {translate("circulationConfiguration")}
          </div>
          <select
            className="p-3 border border-gray-300 rounded-md"
            value={selectedAlternative}
            onChange={(e) =>
              setSelectedAlternative(e.target.value as AlternativeCombination)
            }
          >
            {validAlts.map((alternative: AlternativeCombination) => (
              <option key={alternative} value={alternative}>
                {translate(
                  altCombinationToTranslationKey[
                    alternative
                  ] as keyof typeof translate
                )}
              </option>
            ))}
          </select>
        </label>
      </div>
      <div>
        <ul className="list-disc pl-5">
          {getMatrixTranslations(selectedResultsType, selectedAlternative)?.map(
            (result: string) => (
              <li key={result} className="pl-2">
                {translate(result as keyof typeof translate)}
              </li>
            )
          )}
        </ul>
      </div>
    </div>
  );
}
